// Centralized fetch wrapper. JWT auto-injected from localStorage.
//
// Note on 401 handling: per [feedback_tracon_401_only_for_auth_failure],
// the old TRACON wiped JWT + reloaded on ANY 401 — that's the auto-logout
// loop bug. Here we only dispatch `auth:expired` when the response body
// explicitly identifies the failure as token-related. Other 401s (rate
// limits, scoped denies) just throw and let the caller handle them.
//
// 2026-05-23: added diagnostics to chase "TRACON locks up while idle"
// reports. Every failure logs with timestamp + path + status + body to
// the console, and window.TRACON_DEBUG accumulates a rolling history
// so the broker can inspect post-hoc by typing `TRACON_DEBUG` in the
// console. Non-token failures also fire a toast so silent background
// errors become visible — the lockup symptom was usually "screen looks
// alive but every fetch is throwing into the void."

import { recordReadStore } from './readStoreNotice.js';

export class ApiError extends Error {
  constructor(code, status, body) {
    super(code);
    this.code = code;
    this.status = status;
    this.body = body;
  }
}

// Matches both machine codes AND the actual human strings the server
// emits from routes/tracon.routes.js:307 requireAuth middleware.
// Without the human strings, a stale JWT 401 silently fails the page
// instead of redirecting to /login (caught 2026-05-19 — broker
// was stuck on Command Center with "Invalid or expired token" and no
// auto-redirect).
const TOKEN_FAILURE_CODES = new Set([
  'token_expired',
  'invalid_token',
  'no_token',
  'auth_required',
  'Invalid or expired token',
  'Missing token',
  // personal-logins (PLAN R1): each new 401 also carries the legacy string above; the codes are
  // named too so a body that ever drops it still sends the broker to /login.
  'user_disabled',
  'user_unknown',
  'session_revoked',
  'shared_login_off',
]);

function isTokenFailure(body) {
  if (!body || typeof body !== 'object') return false;
  const candidates = [body.code, body.error, body.message].filter(
    (s) => typeof s === 'string'
  );
  return candidates.some((s) => TOKEN_FAILURE_CODES.has(s));
}

// Rolling debug state — keep last 50 failures + a success counter so
// the broker can answer "is the app silently broken?" by inspecting
// window.TRACON_DEBUG in the console.
const DEBUG_FAILURES_MAX = 50;
if (typeof window !== 'undefined' && !window.TRACON_DEBUG) {
  window.TRACON_DEBUG = {
    successCount: 0,
    failures: [], // {ts, path, status, error, body}
    lastSuccessAt: null,
    lastFailureAt: null,
  };
}

// Suppress toast spam — if the same path fails repeatedly, only toast
// once per 3s for that path. Console logs every failure regardless.
const lastToastByPath = new Map();
function shouldToast(path) {
  const now = Date.now();
  const prev = lastToastByPath.get(path) || 0;
  if (now - prev < 3000) return false;
  lastToastByPath.set(path, now);
  return true;
}

// Error codes whose caller renders its OWN richer toast in a catch block.
// For these, api.js stays silent on the user-facing toast so the generic
// "API 400 /path: code" message doesn't bury (or dedup-win over) the
// caller's specific one. Diagnostics (console + TRACON_DEBUG) still fire
// for every 4xx — only the generic toast is gated. Add codes here as we
// find more caller-handled cases (quote-entry collisions, intake
// validation, duplicate-email guard, etc.).
const CALLER_HANDLED_ERRORS = new Set([
  'not_ready', // /renew + /pre-stage(initiate_after) → PreStageRenewalSheet
  'PRESENT_REFERRED', // present-quotes 409 → PresentQuotesSheet renders it on the card
  'QUOTE_NOT_PRESENTABLE', // present gate 422 (decision 13): every present door's caller renders the server's list (present sheet card, entry sheet, Present selected, record bind)
  'SWEEP_AMOUNT_MISMATCH', // sweep/allocate confirm 409 → MoveConfirm renders both figures
  'SWEEP_ROWS_NOT_READY', // sweep/allocate confirm 409 → the screen reloads its list
  'registry_confirmed', // person edit door 409 (people-create-door C4): PersonIdentityForm shows the registry name and asks the reason
  // The deal's document door (paperwork-1 L1b-B1): the drop dialog and "Wrong type? Change it" render the server's sentence on the row.
  'OUR_OWN_PAPER', // 409 a carrier kind for NDEMNIFLY's own paper (R5)
  'LINKED_TO_TRANSACTION', // 409 a re-type of a document linked to a ledger row
  'IN_A_REPLACEMENT_CHAIN', // 409 a re-type of a replaced or replacing document
  'APPLICATION_HAS_ITS_OWN_DOOR', // 409 a re-type to application that no filed application names
  // N5 (2026-11-01): the re-type to and from the signed application; the row and its toast say the server's sentence.
  'APPLICATION_ON_FILE', // 409 a re-type of a filed application's copy that is still filed (the door un-files it first)
  'APPLICATION_NEEDS_ITS_POLICY', // 409 an account document re-typed to the signed application
  'IMPORT_UNNAMED', // 409 an unnamed import re-typed to the signed application (the naming queue files it)
]);

// The v3 screens' own codes, silent ONLY on /api/v3 calls (rebase onto main,
// 2026-10-05). Main's classic routes answer 503 store_unavailable too
// (tracon.routes.js answerStoreUnavailable), and there the generic toast is the
// only message: in the global set above it would make a failed classic read
// look like an empty one.
const V3_CALLER_HANDLED_ERRORS = new Set([
  'v3_api_disabled', // /api/v3 404 while the flag is off: v3.service renders its own fixture badge (DEV) or error
  'store_unavailable', // /api/v3 503 (docs/v3-account-api.md Conventions): the account page prints the code, the palette says search is unavailable; one message, not two
  'stated_value_protected', // /api/v3 policy-number 409: the input renders who stated the number inline
  'broker_jwt_required', // /api/v3 policy-number 401 with an API key and no broker: the input says sign in
  'already_confirmed', // /api/v3 documents confirm 409: the drop zone row says already filed
  'policy_not_on_account', // /api/v3 documents 400: the confirm sheet says so beside the term picker
  'on_binder_needs_binder', // /api/v3 documents confirm 400: the sheet renders the word
  'reason_required', // /api/v3 documents reconfirm 400: the sheet renders the word
  // The link doors (account-people-door T27): the Dialog renders the server's sentence (body.message).
  'account_people_not_record', // 409 while v3.write_store.account_people is zoho
  'account_people_mode_unknown', // 503 the switch could not be read; nothing written
  'primary_contact_door', // 400 a primary contact is named through the primary-contact door
  'primary_contact_end_refused', // 409 a primary contact link ends by naming the next primary
  'main_not_linked', // 409 main needs an open link on that account
  'agency_mailbox', // 409 the agency mailbox is not a person
  // The rewrite link door (main batch 2026-11-01): the term's Link control renders the word beside its buttons.
  'rewrite_exists', 'rewrite_of_other', 'rewrite_cycle', 'already_follows', 'source_already_renewed', 'no_deal_id',
]);
const callerHandled = (path, code, bodyCode) => CALLER_HANDLED_ERRORS.has(code) || CALLER_HANDLED_ERRORS.has(bodyCode)
  || (String(path).startsWith('/api/v3/') && (V3_CALLER_HANDLED_ERRORS.has(code) || V3_CALLER_HANDLED_ERRORS.has(bodyCode)));

// Every request gets an AbortController timeout so a socket killed by
// laptop sleep settles instead of hanging forever (the inFlight dedup
// slot only frees on settle — see PERF-CACHE-RESILIENCE-SPEC.md Phase 1).
// Per-call override via options.timeoutMs (pipeline/search pass 90s
// until backend SWR lands).
const DEFAULT_TIMEOUT_MS = 30_000;

async function apiCall(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, expected = null, ...fetchOptions } = options;
  const token = localStorage.getItem('tracon_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...fetchOptions.headers,
  };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(path, { ...fetchOptions, headers, signal: controller.signal });
  } catch (networkErr) {
    if (controller.signal.aborted) {
      const ts = new Date().toISOString();
      console.error('[TRACON_DEBUG] timeout', { ts, path, timeoutMs });
      if (typeof window !== 'undefined') {
        window.TRACON_DEBUG.failures.unshift({ ts, path, status: 0, error: `timeout after ${timeoutMs}ms` });
        window.TRACON_DEBUG.failures.length = Math.min(window.TRACON_DEBUG.failures.length, DEBUG_FAILURES_MAX);
        window.TRACON_DEBUG.lastFailureAt = ts;
        if (shouldToast(path)) {
          import('@/components/ui/toaster.jsx').then((m) =>
            m.toast.error?.(`Timeout: ${path.split('?')[0]} — server slow or unreachable`)
          ).catch(() => {});
        }
      }
      throw new ApiError('timeout', 0, { path });
    }
    // Connection refused, DNS, offline, CORS — fetch itself threw.
    const ts = new Date().toISOString();
    console.error('[TRACON_DEBUG] network error', { ts, path, error: networkErr?.message });
    if (typeof window !== 'undefined') {
      window.TRACON_DEBUG.failures.unshift({ ts, path, status: 0, error: networkErr?.message || 'network' });
      window.TRACON_DEBUG.failures.length = Math.min(window.TRACON_DEBUG.failures.length, DEBUG_FAILURES_MAX);
      window.TRACON_DEBUG.lastFailureAt = ts;
      if (shouldToast(path)) {
        // Lazy import to avoid a circular dep (toaster → app → api → toaster).
        import('@/components/ui/toaster.jsx').then((m) =>
          m.toast.error?.(`Network: ${path.split('?')[0]} — ${networkErr?.message || 'fetch failed'}`)
        ).catch(() => {});
      }
    }
    throw networkErr;
  } finally {
    clearTimeout(timer);
  }
  // A response read from TRACON's own tables names what it does not carry
  // (X-TRACON-Not-Carried); hand it to the notice the shell renders, on any
  // status (a 501 not_carried carries it too). Review A-R5.
  const readStore = res.headers && typeof res.headers.get === 'function' ? res.headers.get('X-TRACON-Read-Store') : null;
  if (readStore) recordReadStore(path, readStore, res.headers.get('X-TRACON-Not-Carried'), res.status);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const ts = new Date().toISOString();
    const code = body.error || body.code || `http_${res.status}`;
    // AN EXPECTED ANSWER IS NOT A FAILURE (lane C review C-R9). A caller that
    // names a status and code it expects (options.expected, [{ status, code }])
    // gets the ApiError to branch on, and nothing is logged, recorded in
    // TRACON_DEBUG or toasted: the V2 shell's flag probe answers 404
    // v3_api_disabled on every load while the flag is off, and that is the
    // answer "off", not a broken app. Only that exact pair is quiet; any other
    // failure of the same call is recorded as before.
    // An entry may also carry `body`, a predicate the whole body must pass, so a caller can expect one exact answer and no other.
    if (Array.isArray(expected) && expected.some((x) => x && x.status === res.status && (x.code === code || x.code === body.code) && (typeof x.body !== 'function' || x.body(body)))) {
      throw new ApiError(code, res.status, body);
    }
    const tokenFail = res.status === 401 && isTokenFailure(body);
    console.error('[TRACON_DEBUG] api error', {
      ts, path, status: res.status, code, body, tokenFail,
    });
    if (typeof window !== 'undefined') {
      window.TRACON_DEBUG.failures.unshift({ ts, path, status: res.status, error: code, body });
      window.TRACON_DEBUG.failures.length = Math.min(window.TRACON_DEBUG.failures.length, DEBUG_FAILURES_MAX);
      window.TRACON_DEBUG.lastFailureAt = ts;
    }
    if (tokenFail) {
      window.dispatchEvent(new Event('auth:expired'));
    } else if (
      typeof window !== 'undefined' &&
      // `code` is body.error first, which on most routes is the human sentence,
      // so a machine code the caller handles is matched on body.code as well.
      !CALLER_HANDLED_ERRORS.has(code) &&
      !CALLER_HANDLED_ERRORS.has(body.code) &&
      !callerHandled(path, code, body.code) &&
      shouldToast(path)
    ) {
      // Non-token failures fire a toast so the broker sees them.
      // Token failures don't (the auth:expired listener handles the
      // redirect; a toast on top would be noise). Caller-handled codes
      // don't either — the caller's catch renders a richer toast, and
      // the Set check runs before shouldToast() so we don't consume its
      // debounce slot when suppressing.
      import('@/components/ui/toaster.jsx').then((m) =>
        m.toast.error?.(`API ${res.status} ${path.split('?')[0]}: ${code}`)
      ).catch(() => {});
    }
    throw new ApiError(code, res.status, body);
  }
  if (typeof window !== 'undefined') {
    window.TRACON_DEBUG.successCount++;
    window.TRACON_DEBUG.lastSuccessAt = new Date().toISOString();
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path, options) => apiCall(path, options),
  post: (path, body, options) => apiCall(path, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (path, body, options) => apiCall(path, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  patch: (path, body, options) => apiCall(path, { ...options, method: 'PATCH', body: JSON.stringify(body) }),
  del: (path, options) => apiCall(path, { ...options, method: 'DELETE' }),
};
